import { memo, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { PerformanceProfiler } from '../../../components/PerformanceProfiler';
import {
  Search,
  PlayCircle,
  Calendar,
  CheckCircle,
  Filter,
} from 'lucide-react';
import {
  Card,
  Flex,
  Group,
  Text,
  TextInput,
  Box,
  Select,
  Button,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';

type StatusType = 'in-corso' | 'programmate' | 'chiuse';

interface SessionsFiltersProps {
  selectedStatuses: ('in-progress' | 'queued' | 'completed')[];
  onStatusChange: (
    statuses: ('in-progress' | 'queued' | 'completed')[]
  ) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  categoryValue: string | null;
  onCategoryChange: (value: string | null) => void;
  categoryOptions: Array<{ value: string; label: string }>;
  dateRangeValue: [Date | null, Date | null];
  onDateRangeChange: (value: [Date | null, Date | null]) => void;
}

// Mapping da status UI a status backend
const statusMapping = {
  'in-corso': 'in-progress',
  programmate: 'queued',
  chiuse: 'completed',
} as const;

const reverseStatusMapping = {
  'in-progress': 'in-corso',
  queued: 'programmate',
  completed: 'chiuse',
} as const;

// Componente memoizzato per singolo Status Button
const StatusButton = memo<{
  status: StatusType;
  label: string;
  icon: ReactNode;
  isSelected: boolean;
  onToggle: (status: StatusType) => void;
}>(({ status, label, icon, isSelected, onToggle }) => {
  const handleClick = useCallback(() => {
    onToggle(status);
  }, [status, onToggle]);

  return (
    <Button
      variant={isSelected ? 'light' : 'subtle'}
      radius='sm'
      size='sm'
      px='md'
      py='xs'
      fw={500}
      bg={isSelected ? undefined : 'var(--mantine-color-gray-0)'}
      c={isSelected ? undefined : 'var(--mantine-color-gray-7)'}
      style={{
        border: isSelected ? 'none' : '1px solid var(--mantine-color-gray-3)',
      }}
      leftSection={icon}
      onClick={handleClick}
    >
      {label}
    </Button>
  );
});

StatusButton.displayName = 'StatusButton';

// Componente memoizzato per Status Filter Buttons
const StatusFilters = memo<{
  selectedStatuses: ('in-progress' | 'queued' | 'completed')[];
  onStatusChange: (
    statuses: ('in-progress' | 'queued' | 'completed')[]
  ) => void;
}>(({ selectedStatuses, onStatusChange }) => {
  // Converti status backend a status UI
  const uiStatuses = useMemo(() => {
    return selectedStatuses
      .map(s => reverseStatusMapping[s as keyof typeof reverseStatusMapping])
      .filter(Boolean) as StatusType[];
  }, [selectedStatuses]);

  const handleToggle = useCallback(
    (status: StatusType) => {
      const newStatuses = uiStatuses.includes(status)
        ? uiStatuses.filter(s => s !== status).map(s => statusMapping[s])
        : [...uiStatuses, status].map(s => statusMapping[s]);

      // Se rimuovendo questo status non rimane nessuno, mantieni almeno 'in-progress'
      if (newStatuses.length === 0) {
        onStatusChange(['in-progress']);
      } else {
        onStatusChange(
          newStatuses as ('in-progress' | 'queued' | 'completed')[]
        );
      }
    },
    [uiStatuses, onStatusChange]
  );

  // Se nessuno è selezionato, seleziona "in-corso" di default
  useEffect(() => {
    if (selectedStatuses.length === 0) {
      onStatusChange(['in-progress']);
    }
  }, [selectedStatuses.length, onStatusChange]);

  const isInCorsoSelected = useMemo(
    () => uiStatuses.includes('in-corso'),
    [uiStatuses]
  );
  const isProgrammateSelected = useMemo(
    () => uiStatuses.includes('programmate'),
    [uiStatuses]
  );
  const isChiuseSelected = useMemo(
    () => uiStatuses.includes('chiuse'),
    [uiStatuses]
  );

  return (
    <Group gap='xs' wrap='nowrap'>
      <StatusButton
        status='in-corso'
        label='In corso'
        icon={<PlayCircle size={16} />}
        isSelected={isInCorsoSelected}
        onToggle={handleToggle}
      />
      <StatusButton
        status='programmate'
        label='Programmate'
        icon={<Calendar size={16} />}
        isSelected={isProgrammateSelected}
        onToggle={handleToggle}
      />
      <StatusButton
        status='chiuse'
        label='Completate'
        icon={<CheckCircle size={16} />}
        isSelected={isChiuseSelected}
        onToggle={handleToggle}
      />
    </Group>
  );
});

StatusFilters.displayName = 'StatusFilters';

// Componente memoizzato per Categoria App Select
const CategoriaAppSelect = memo<{
  value: string | null;
  onChange: (value: string | null) => void;
  options: Array<{ value: string; label: string }>;
}>(({ value, onChange, options }) => {
  return (
    <Select
      placeholder='Tutte le categorie'
      data={options}
      value={value}
      onChange={onChange}
      clearable
      size='sm'
      radius='sm'
    />
  );
});

CategoriaAppSelect.displayName = 'CategoriaAppSelect';

// Componente memoizzato per Data Creazione Picker (Range)
const DataCreazionePicker = memo<{
  value: [Date | null, Date | null];
  onChange: (value: [Date | null, Date | null]) => void;
}>(({ value, onChange }) => {
  return (
    <DatePickerInput
      type='range'
      placeholder='Data creazione Ultimi 30 giorni'
      value={value}
      onChange={val => {
        if (Array.isArray(val) && val.length === 2) {
          onChange([val[0] as Date | null, val[1] as Date | null]);
        } else {
          onChange([null, null]);
        }
      }}
      size='sm'
      radius='sm'
      clearable
    />
  );
});

DataCreazionePicker.displayName = 'DataCreazionePicker';

// Componente memoizzato per Search Input
const SearchInput = memo<{
  value: string;
  onChange: (value: string) => void;
}>(({ value, onChange }) => {
  return (
    <TextInput
      placeholder='Cerca per utente, ID sessione, dispositivo...'
      value={value}
      onChange={e => onChange(e.currentTarget.value)}
      radius='sm'
      size='sm'
      variant='filled'
      leftSection={<Search size={16} color='var(--mantine-color-gray-6)' />}
    />
  );
});

SearchInput.displayName = 'SearchInput';

// Componente principale
export const SessionsFilters = memo<SessionsFiltersProps>(
  ({
    selectedStatuses,
    onStatusChange,
    searchValue,
    onSearchChange,
    categoryValue,
    onCategoryChange,
    categoryOptions,
    dateRangeValue,
    onDateRangeChange,
  }) => {
    return (
      <PerformanceProfiler id='SessionsFilters'>
        <Card shadow='sm' withBorder mb='md' radius='sm' p='lg'>
          <Flex direction='column' gap='md'>
            <Flex justify='space-between' align='center' wrap='wrap' gap='md'>
              <Flex mr={2} align='center' gap='xs'>
                <Filter
                  size={16}
                  color='var(--mantine-color-gray-5)'
                  style={{ verticalAlign: 'middle' }}
                />
                <Text size='sm' c='' fw={500}>
                  Filtri rapidi
                </Text>
              </Flex>

              {/* Status Filter Buttons */}
              <StatusFilters
                selectedStatuses={selectedStatuses}
                onStatusChange={onStatusChange}
              />
            </Flex>

            {/* Riga inferiore: Search e Dropdowns */}
            <Flex
              justify='space-between'
              align='center'
              wrap='wrap'
              gap='md'
              direction={{ base: 'column', sm: 'row' }}
            >
              {/* Search Input - prende più spazio */}
              <Box flex={1} miw={300}>
                <SearchInput value={searchValue} onChange={onSearchChange} />
              </Box>

              {/* Dropdown Buttons - raggruppati a destra */}
              <Group gap='xs' wrap='wrap'>
                <CategoriaAppSelect
                  value={categoryValue}
                  onChange={onCategoryChange}
                  options={categoryOptions}
                />
                <DataCreazionePicker
                  value={dateRangeValue}
                  onChange={onDateRangeChange}
                />
              </Group>
            </Flex>
          </Flex>
        </Card>
      </PerformanceProfiler>
    );
  }
);

SessionsFilters.displayName = 'SessionsFilters';
